
import React from "react";
import ReactDOM from "react-dom/client";
import "./App.css";
import "./index.css";
import reportWebVitals from "./reportWebVitals";
import Routerpage from "./Routerpage";
import { ModalContextProvider } from "./context/context";
import { SelectedItemProvider } from "./context/store";
import { LoginProvider } from "./context/contextlogin";
import { HelmetProvider } from "react-helmet-async";
import { QueryClientProvider } from "@tanstack/react-query";
import { queryClient } from "./queryClient/queryClient";
import { LocationProvider } from "./context/LocationContext";
import { NavbarProvider } from "./context/NavbarContext";
import LocationPermissionWrapper from "./context/LocationPermissionWrapper";
import { LocationPermissionProvider } from "./context/LocationPermissionContext";
import { CompareProvider } from "./context/CompareContext";
import "./api/customerHttpClient";
import {
  normalizeCustomerUserDataInStorage,
  installUserDataStorageGuard,
} from "./auth/customerIdentity";
import VpnGate from "./dev/VpnGate";
import { installPopupTracker } from "./Discover-Profile/popupTracker";

const hostname = String(window.location.hostname || "").toLowerCase();
const isLocalhost = hostname === "localhost" || hostname === "127.0.0.1" || hostname === "[::1]";
const isVpnWebHost = hostname === "dev.partywitty.com";
const useVpnNetworkEndpoints = isLocalhost || isVpnWebHost;
const fallbackApiBaseUrl = useVpnNetworkEndpoints ? "https://dashboard.partywitty.com" : "https://admin.partywitty.com";
const fallbackNodeApiBaseUrl = useVpnNetworkEndpoints ? "https://vpnnode.partywitty.com" : "https://node.partywitty.com";
window.API_BASE_URL = String(process.env.REACT_APP_API_BASE_URL || fallbackApiBaseUrl).replace(/\/+$/, "");
const absoluteNodeApiBaseUrl = String(process.env.REACT_APP_NODE_API_BASE_URL || fallbackNodeApiBaseUrl).replace(/\/+$/, "");
// On localhost, route Node + customer-auth through the dev proxy so the session
// cookie is first-party (see setupProxy.js / utils.js). Keeps login persistent.
window.NODE_API_BASE_URL = isLocalhost ? "/__pw_node" : absoluteNodeApiBaseUrl;
window.CUSTOMER_API_BASE_URL = isLocalhost ? "/__pw_api" : window.API_BASE_URL;
// Sanitise every userData write app-wide (guard first), then repair whatever is
// already stored. Together these guarantee userData.id is always a valid identity.
installUserDataStorageGuard();
normalizeCustomerUserDataInStorage();

// UTM / click-id attribution capture — fire ONCE per browser session on the real
// landing URL. Reuses the existing backend endpoint (APIs/Web/TrackUtm), which
// stashes the params in the PHP session for later conversion attribution
// (UtmJourneyLogger). Best-effort and fully isolated: it never blocks or breaks
// app startup. Runs here (not in a component effect) so React StrictMode's dev
// double-invoke can't fire it twice; the sessionStorage flag stops refreshes.
(function captureUtmAttributionOnce() {
  try {
    const search = window.location.search || "";
    if (!/[?&](utm_[a-z]+|fbclid|fbp|fbc)=/i.test(search)) return;
    if (sessionStorage.getItem("pw_utm_captured") === "1") return;
    sessionStorage.setItem("pw_utm_captured", "1");
    fetch(String(window.CUSTOMER_API_BASE_URL || "") + "/APIs/Web/TrackUtm" + search, {
      method: "GET",
      credentials: "include",
      keepalive: true,
    }).catch(() => {});
  } catch (e) {
    /* attribution capture must never affect the app */
  }
})();

// Popup-open tracker for the Arsenal "Popups → Live" feed — dev / VPN-test only
// (never for real production users). Best-effort; must not affect startup.
try {
  if (useVpnNetworkEndpoints || process.env.NODE_ENV !== "production") {
    installPopupTracker();
  }
} catch (e) {
  /* tracker must never break the app */
}

const root = ReactDOM.createRoot(document.getElementById("root"));

root.render(
  <React.StrictMode>
    <VpnGate>
      <QueryClientProvider client={queryClient}>
        <HelmetProvider>
          <LocationPermissionProvider>
            <LocationPermissionWrapper>
              <ModalContextProvider>
                <SelectedItemProvider>
                  <LoginProvider>
                    <LocationProvider>
                      <NavbarProvider>
                        <CompareProvider>
                          <Routerpage />
                        </CompareProvider>
                      </NavbarProvider>
                    </LocationProvider>
                  </LoginProvider>
                </SelectedItemProvider>
              </ModalContextProvider>
            </LocationPermissionWrapper>
          </LocationPermissionProvider>
        </HelmetProvider>
        {/* <ReactQueryDevtools initialIsOpen={false} /> */}
      </QueryClientProvider>
    </VpnGate>
  </React.StrictMode>
);

reportWebVitals();
